/**
 * React Hook for Enhanced Notifications
 * Provides real-time notification data and management functions
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import enhancedNotificationService from '../utils/enhancedNotificationService.js';
import apiService from '../utils/apiService.js';

export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const listenerRef = useRef(null);

  // Initialize service and set up listeners
  useEffect(() => {
    let mounted = true;

    const initializeService = async () => {
      try {
        console.log('📢 Initializing notifications hook...');
        
        // Initialize the service if not already done
        await enhancedNotificationService.initialize();

        // Ensure we explicitly load notifications to guarantee fresh data
        // This handles cases where service was already initialized but data might be stale
        await enhancedNotificationService.loadNotifications();
        
        if (!mounted) return;

        // Set up listener for notification events
        listenerRef.current = enhancedNotificationService.addListener((event, data) => {
          if (!mounted) return;

          console.log('📢 Notification event received:', event, data);

          switch (event) {
            case 'notifications_loaded':
              setNotifications(data || []);
              setUnreadCount(enhancedNotificationService.getUnreadCount());
              setLastUpdate(Date.now());
              setError(null);
              setIsLoading(false);
              break;

            case 'notification_created':
              setNotifications(prev => [data, ...prev]);
              setUnreadCount(prev => prev + 1);
              setLastUpdate(Date.now());
              break;

            case 'notification_updated':
              setNotifications(prev => 
                prev.map(n => n.id === data.id ? { ...n, ...data } : n)
              );
              setUnreadCount(enhancedNotificationService.getUnreadCount());
              setLastUpdate(Date.now());
              break;

            case 'notification_deleted':
              setNotifications(prev => prev.filter(n => n.id !== data));
              setUnreadCount(enhancedNotificationService.getUnreadCount());
              setLastUpdate(Date.now());
              break;

            case 'notification_marked_read':
              setNotifications(prev => 
                prev.map(n => 
                  n.id === data 
                    ? { ...n, read: true, read_at: new Date().toISOString(), isRead: true }
                    : n
                )
              );
              setUnreadCount(enhancedNotificationService.getUnreadCount());
              setLastUpdate(Date.now());
              break;

            case 'all_notifications_marked_read':
              setNotifications(data || []);
              setUnreadCount(0);
              setLastUpdate(Date.now());
              break;

            case 'notifications_marked_read':
              setNotifications(prev => 
                prev.map(n => 
                  data.includes(n.id)
                    ? { ...n, read: true, read_at: new Date().toISOString(), isRead: true }
                    : n
                )
              );
              setUnreadCount(enhancedNotificationService.getUnreadCount());
              setLastUpdate(Date.now());
              break;

            case 'unread_count_changed':
              setUnreadCount(data);
              setLastUpdate(Date.now());
              break;

            case 'notifications_error':
              setError(data);
              setIsLoading(false);
              break;

            default:
              console.log('Unknown notification event:', event);
          }
        });

        // Get initial data
        const initialNotifications = enhancedNotificationService.getNotifications();
        const initialUnreadCount = enhancedNotificationService.getUnreadCount();
        
        setNotifications(initialNotifications);
        setUnreadCount(initialUnreadCount);
        setLastUpdate(Date.now());
        setIsLoading(false);

        console.log('✅ Notifications hook initialized');
      } catch (err) {
        console.error('❌ Failed to initialize notifications hook:', err);
        if (mounted) {
          setError(err);
          setIsLoading(false);
        }
      }
    };

    initializeService();

    return () => {
      mounted = false;
      if (listenerRef.current) {
        listenerRef.current();
        listenerRef.current = null;
      }
    };
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await enhancedNotificationService.markAsRead(notificationId);
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      setError(err);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      setIsLoading(true);
      await enhancedNotificationService.markAllAsRead();
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await enhancedNotificationService.deleteNotification(notificationId);
    } catch (err) {
      console.error('Failed to delete notification:', err);
      setError(err);
    }
  }, []);

  // Refresh notifications manually
  const refreshNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      await enhancedNotificationService.loadNotifications();
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Handle join request actions (approve/reject)
   * @param {Object} notification - The notification object
   * @param {string} actionType - 'approve' or 'reject'
   */
  const handleJoinAction = useCallback(async (notification, actionType) => {
    try {
      const regId = notification.notification_metadata?.registration_id;
      const orgId = notification.organization_id;

      if (!regId || !orgId) {
        throw new Error('Missing registration or organization ID');
      }

      if (actionType === 'approve') {
        await apiService.organizations.approveJoinRequest(orgId, regId);
      } else {
        await apiService.organizations.rejectJoinRequest(orgId, regId);
      }

      // Mark notification as read after action
      await markAsRead(notification.id);
      
      // Refresh notifications to reflect the change
      await refreshNotifications();

      return { success: true };
    } catch (err) {
      console.error(`Failed to ${actionType} join request:`, err);
      return { success: false, error: err.message };
    }
  }, [markAsRead, refreshNotifications]);

  // Get notifications by type
  const getNotificationsByType = useCallback((type) => {
    return notifications.filter(n => n.type === type);
  }, [notifications]);

  // Get unread notifications
  const getUnreadNotifications = useCallback(() => {
    return notifications.filter(n => !n.read && !n.read_at && !n.isRead);
  }, [notifications]);

  // Get recent notifications (last 24 hours)
  const getRecentNotifications = useCallback(() => {
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return notifications.filter(n => new Date(n.created_at || n.createdAt) > oneDayAgo);
  }, [notifications]);

  // Format notification time
  const formatNotificationTime = useCallback((timestamp) => {
    if (!timestamp) return 'Unknown time';
    
    const now = Date.now();
    const notificationTime = new Date(timestamp).getTime();
    const diffMs = now - notificationTime;
    
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMinutes < 1) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return new Date(timestamp).toLocaleDateString();
    }
  }, []);

  // Get notification priority color
  const getNotificationPriorityColor = useCallback((priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
      case 'urgent':
        return 'text-red-500';
      case 'medium':
        return 'text-yellow-500';
      case 'low':
        return 'text-blue-500';
      default:
        return 'text-gray-500';
    }
  }, []);

  // Get notification type icon
  const getNotificationTypeIcon = useCallback((type) => {
    switch (type?.toLowerCase()) {
      case 'task':
      case 'task_assigned':
      case 'task_completed':
        return 'CheckSquare';
      case 'project':
      case 'project_update':
        return 'Folder';
      case 'team':
      case 'team_member_added':
        return 'Users';
      case 'system':
        return 'Settings';
      case 'welcome':
        return 'Heart';
      case 'reminder':
        return 'Clock';
      default:
        return 'Bell';
    }
  }, []);

  return {
    // Data
    notifications,
    unreadCount,
    isLoading,
    error,
    lastUpdate,
    
    // Actions
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications,
    handleJoinAction,
    
    // Helpers
    getNotificationsByType,
    getUnreadNotifications,
    getRecentNotifications,
    formatNotificationTime,
    getNotificationPriorityColor,
    getNotificationTypeIcon,
  };
};

export default useNotifications;
